
import { getPermanentTemplate } from '@/api';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import * as routeNames from '@/router/routeNames';
import * as Sentry from '@sentry/vue';

import { PermanentTemplate, PermanentTemplateStop } from '@/models/permanentJob';
import { format, parseISO } from 'date-fns';
import { Component, Vue } from 'vue-property-decorator';
import { notificationErrorText } from '@/utils/PortalConstants';

@Component({
  components: {
    BackButton,
  },
})
export default class PermanentJobDetail extends Vue {
  private loading = false;
  private template: PermanentTemplate | null = null;

  async mounted() {
    const id = parseInt(this.$route.params.id, 10);
    if (!id || isNaN(id)) {
      this.$router.push({ name: routeNames.PERMANENT_JOBS_LIST });
      return;
    }
    await this.fetchTemplate(id);
  }

  private async fetchTemplate(id: number) {
    this.loading = true;
    try {
      this.template = await getPermanentTemplate(id);
    } catch (err: any) {
      Sentry.captureException(err);
      this.$notify({
        group: 'network',
        text: notificationErrorText,
        type: 'warn',
        duration: 3000,
      });
    } finally {
      this.loading = false;
    }
  }

  private formatDate(dateStr: string | null): string {
    if (!dateStr) {
      return '-';
    }
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  }

  get noDaysSelected(): boolean {
    if (!this.template) {
      return true;
    }
    return (
      !this.template.required_on_monday &&
      !this.template.required_on_tuesday &&
      !this.template.required_on_wednesday &&
      !this.template.required_on_thursday &&
      !this.template.required_on_friday &&
      !this.template.required_on_saturday &&
      !this.template.required_on_sunday
    );
  }

  get excludedDates(): string[] {
    if (!this.template) {
      return [];
    }
    return [this.template.not_on1, this.template.not_on2, this.template.not_on3, this.template.not_on4]
      .filter((d): d is string => !!d)
      .map(d => this.formatDate(d));
  }

  get hasExcludedDates(): boolean {
    return this.excludedDates.length > 0;
  }

  get extraDates(): string[] {
    if (!this.template) {
      return [];
    }
    return [this.template.also_on1, this.template.also_on2, this.template.also_on3, this.template.also_on4]
      .filter((d): d is string => !!d)
      .map(d => this.formatDate(d));
  }

  get hasExtraDates(): boolean {
    return this.extraDates.length > 0;
  }

  private hasDriverOverrides(stop: PermanentTemplateStop): boolean {
    return !!(
      stop.mon_driver ||
      stop.tue_driver ||
      stop.wed_driver ||
      stop.thu_driver ||
      stop.fri_driver ||
      stop.sat_driver ||
      stop.sun_driver
    );
  }

  private holidayRuleLabel(rule: string): string {
    const labels: Record<string, string> = { C: 'Cancel', P: 'Proceed', N: 'Next day', M: 'Move week' };
    return labels[rule] || rule;
  }

  private holidayRuleColor(rule: string): string {
    const colors: Record<string, string> = { C: 'red', P: 'green', N: 'blue', M: 'orange' };
    return colors[rule] || 'grey';
  }

  private driverOptionLabel(option: string): string {
    const labels: Record<string, string> = { prealloc: 'Pre-allocate', allocate: 'Allocate', complete: 'Complete' };
    return labels[option] || option;
  }

  private displayTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      C: 'City',
      S: 'Street',
      N: 'Name',
      U: 'Suite',
      E: 'Etc1',
      F: 'Etc2',
    };
    return labels[type] || type;
  }

  private noOfTimesLabel(n: number): string {
    if (n === -1) {
      return 'Indefinite';
    }
    if (n === 0) {
      return 'Disabled';
    }
    return `${n} times left`;
  }

  private noOfTimesColor(n: number): string {
    if (n === -1) {
      return 'purple';
    }
    if (n === 0) {
      return 'orange';
    }
    return 'green';
  }

  private noOfTimesIcon(n: number): string {
    if (n === -1) {
      return 'mdi-infinity';
    }
    if (n === 0) {
      return 'mdi-pause-circle-outline';
    }
    return 'mdi-counter';
  }
}
